import * as React from "react"
import type { DateRange } from "react-day-picker"
import { format } from "date-fns"
import { Pencil, Trash2 } from "lucide-react"

import { cn } from "@/lib/utils"
import type { Account, Category } from "@/types"
import type { CollectionTransaction } from "../../types"
import { Badge } from "@/shared/ui/atoms/Badge"
import { Button } from "@/shared/ui/atoms/Button"
import { formatCurrency } from "@/lib/utils/formatCurrency"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/atoms/Table"
import { EmptyCollection } from "../atoms/EmptyCollection"
import { formatTransactionDate } from "../../utils/formatters"
import {
  TableFilters,
  type MonthOption,
  type CategoryOption,
  type AccountOption,
} from "../molecules/TableFilters"
import { TableSkeleton } from "@/shared/ui/molecules/TableSkeleton"
import { useIsMobile } from "@/shared/hooks/useMediaQuery"

interface IncomeCollectionProps {
  transactions: CollectionTransaction[]
  accounts: Map<number, Account>
  categories: Map<number, Category>
  amountsVisible?: boolean
  availableMonths?: MonthOption[]
  initialMonth?: string
  selectedMonth?: string
  isLoading?: boolean
  isFetching?: boolean
  onMonthChange?: (month: string) => void
  onEdit?: (transaction: CollectionTransaction) => void
  onDelete?: (transaction: CollectionTransaction) => void
}

export const IncomeCollection = React.memo(function IncomeCollection({
  transactions,
  accounts,
  categories,
  amountsVisible = true,
  availableMonths,
  initialMonth,
  selectedMonth: propSelectedMonth,
  isLoading = false,
  isFetching = false,
  onMonthChange,
  onEdit,
  onDelete,
}: IncomeCollectionProps) {
  // Extract or build available months
  const monthOptions = React.useMemo<MonthOption[]>(() => {
    if (availableMonths && availableMonths.length > 0) {
      return availableMonths
    }

    const monthSet = new Set<string>()
    for (const tx of transactions) {
      if (tx.date) {
        const monthKey = tx.date.slice(0, 7) // "YYYY-MM"
        if (/^\d{4}-\d{2}$/.test(monthKey)) {
          monthSet.add(monthKey)
        }
      }
    }

    const sortedMonths = Array.from(monthSet).sort((a, b) => b.localeCompare(a))
    const monthNames = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ]

    return [
      { value: "ALL", label: "Todos los meses" },
      ...sortedMonths.map((m) => {
        const [year, month] = m.split("-")
        const monthIndex = parseInt(month, 10) - 1
        const monthName = monthNames[monthIndex] || month
        return {
          value: m,
          label: `${monthName} ${year}`,
        }
      }),
    ]
  }, [availableMonths, transactions])

  // Extract all categories & accounts available in the system
  const { categoryOptions, accountOptions } = React.useMemo(() => {
    const catOpts: CategoryOption[] = Array.from(categories.values())
      .map((cat) => ({ id: cat.id, name: cat.name }))
      .sort((a, b) => a.name.localeCompare(b.name))

    const accOpts: AccountOption[] = Array.from(accounts.values())
      .map((acc) => ({ id: acc.id, name: acc.name }))
      .sort((a, b) => a.name.localeCompare(b.name))

    return { categoryOptions: catOpts, accountOptions: accOpts }
  }, [categories, accounts])

  const isMobile = useIsMobile()

  // Filter States (Completely isolated per collection)
  const defaultMonth = React.useMemo(() => {
    if (initialMonth) return initialMonth
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  }, [initialMonth])

  const [localMonth, setLocalMonth] = React.useState<string>(propSelectedMonth ?? defaultMonth)
  const activeMonth = propSelectedMonth !== undefined ? propSelectedMonth : localMonth

  const handleSelectMonth = React.useCallback((val: string) => {
    if (onMonthChange) {
      onMonthChange(val)
    } else {
      setLocalMonth(val)
    }
  }, [onMonthChange])

  const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined)
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>(["ALL"])
  const [selectedAccounts, setSelectedAccounts] = React.useState<string[]>(["ALL"])
  const [searchTerm, setSearchTerm] = React.useState<string>("")
  const deferredSearchTerm = React.useDeferredValue(searchTerm)

  const isCategoriesActive = selectedCategories.length > 0 && !selectedCategories.includes("ALL")
  const isAccountsActive = selectedAccounts.length > 0 && !selectedAccounts.includes("ALL")

  const hasActiveFilters =
    Boolean(dateRange?.from) ||
    activeMonth !== "ALL" ||
    isCategoriesActive ||
    isAccountsActive ||
    searchTerm.trim() !== ""

  const handleResetFilters = () => {
    handleSelectMonth("ALL")
    setDateRange(undefined)
    setSelectedCategories(["ALL"])
    setSelectedAccounts(["ALL"])
    setSearchTerm("")
  }

  // Filter and compute transactions
  const filteredTransactions = React.useMemo(() => {
    return transactions.filter((tx) => {
      // Date Range Filter
      if (dateRange?.from) {
        const fromStr = format(dateRange.from, "yyyy-MM-dd")
        const toStr = dateRange.to ? format(dateRange.to, "yyyy-MM-dd") : fromStr
        const txStartDate = tx.date

        if (txStartDate < fromStr || txStartDate > toStr) {
          return false
        }
      } else if (activeMonth !== "ALL") {
        // Month Filter
        if (!tx.date || !tx.date.startsWith(activeMonth)) {
          return false
        }
      }

      // Multi-Category Filter
      if (isCategoriesActive) {
        if (!selectedCategories.includes(String(tx.category))) {
          return false
        }
      }

      // Multi-Account Filter
      if (isAccountsActive) {
        if (!selectedAccounts.includes(String(tx.account))) {
          return false
        }
      }

      // Search Term Filter
      if (deferredSearchTerm.trim() !== "") {
        const query = deferredSearchTerm.toLowerCase().trim()
        const descMatch = (tx.description || "").toLowerCase().includes(query)
        const catName = tx.category !== undefined ? categories.get(tx.category)?.name?.toLowerCase() || "" : ""
        const catMatch = catName.includes(query)

        if (!descMatch && !catMatch) {
          return false
        }
      }

      return true
    })
  }, [transactions, dateRange, activeMonth, isCategoriesActive, selectedCategories, isAccountsActive, selectedAccounts, deferredSearchTerm, categories])

  if (isLoading && transactions.length === 0) {
    return <TableSkeleton hasCategoryColumn={true} hasAmountColumn={true} />
  }

  if (transactions.length === 0) {
    return <EmptyCollection>No hay ingresos registrados para mostrar.</EmptyCollection>
  }

  return (
    <div className="flex flex-col">
      {/* Filter Toolbar */}
      <TableFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        months={monthOptions}
        selectedMonth={activeMonth}
        onMonthChange={handleSelectMonth}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        categories={categoryOptions}
        selectedCategories={selectedCategories}
        onCategoriesChange={setSelectedCategories}
        accounts={accountOptions}
        selectedAccounts={selectedAccounts}
        onAccountsChange={setSelectedAccounts}
        onResetFilters={handleResetFilters}
        hasActiveFilters={hasActiveFilters}
        totalCount={transactions.length}
        filteredCount={filteredTransactions.length}
      />

      {/* Loading Shimmer Bar on Refetch */}
      {isFetching && (
        <div className="h-[2px] w-full bg-primary/20 overflow-hidden relative z-10">
          <div className="h-full bg-primary animate-pulse w-full" />
        </div>
      )}

      {/* Content View with smooth transition */}
      <div className={cn("transition-opacity duration-200", isFetching && "opacity-60 pointer-events-none")}>
        {/* When no rows match filters */}
        {filteredTransactions.length === 0 ? (
          <div className="p-8 text-center text-xs text-muted-foreground">
            No se encontraron ingresos con los filtros seleccionados.
          </div>
        ) : isMobile ? (
          /* Mobile Touch-Friendly Card List with Controlled Scrollable Viewport */
          <div className="flex flex-col divide-y divide-border/60 max-h-[460px] overflow-y-auto">
          {filteredTransactions.map((transaction) => {
            const categoryName = transaction.category !== undefined ? categories.get(transaction.category)?.name ?? "Sin categoría" : "Sin categoría"
            const account = accounts.get(transaction.account)
            const accountName = account?.name ?? "Cuenta eliminada"
            const accountCurrency = account?.currency || "HNL"
            return (
              <div key={transaction.id} className="flex flex-col gap-2 p-3.5 hover:bg-muted/40 transition-colors">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-sm text-foreground truncate">
                    {transaction.description || "Sin descripción"}
                  </span>
                  <span className="text-xs font-semibold text-emerald-500 shrink-0">
                    {amountsVisible ? (
                      `+ ${formatCurrency(Number(transaction.amount), accountCurrency)}`
                    ) : (
                      "••••••"
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="truncate">{accountName}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Badge variant="secondary" className="text-[11px]">
                      {categoryName}
                    </Badge>
                    <span>{formatTransactionDate(transaction.date)}</span>
                  </div>
                </div>

                {/* Actions for Mobile */}
                <div className="flex items-center justify-end gap-2 pt-1 border-t border-border/40">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
                    onClick={() => onEdit?.(transaction)}
                  >
                    <Pencil className="size-3" />
                    <span>Editar</span>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-[11px] text-muted-foreground hover:text-destructive gap-1"
                    onClick={() => onDelete?.(transaction)}
                  >
                    <Trash2 className="size-3" />
                    <span>Eliminar</span>
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        /* Desktop / Tablet Table with Controlled Scrollable Viewport */
        <div className="max-h-[460px] overflow-y-auto relative">
          <Table className="cartera-table">
            <TableHeader className="sticky top-0 bg-card z-10 shadow-2xs border-b border-border">
              <TableRow>
                <TableHead className="w-[120px]">Fecha</TableHead>
                <TableHead>Concepto</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Cuenta</TableHead>
                <TableHead className="text-right w-[140px]">Importe</TableHead>
                <TableHead className="w-[90px] text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransactions.map((transaction) => {
                const account = accounts.get(transaction.account)
                const accountName = account?.name ?? "Cuenta eliminada"
                const accountCurrency = account?.currency || "HNL"
                return (
                  <TableRow key={transaction.id} className="cartera-table-row">
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatTransactionDate(transaction.date)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {transaction.description || "Sin descripción"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-xs">
                        {transaction.category !== undefined ? categories.get(transaction.category)?.name ?? "Sin categoría" : "Sin categoría"}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium whitespace-nowrap">
                      {accountName}
                    </TableCell>
                    <TableCell className="text-right font-semibold whitespace-nowrap text-emerald-500">
                      {amountsVisible ? (
                        `+ ${formatCurrency(Number(transaction.amount), accountCurrency)}`
                      ) : (
                        "••••••"
                      )}
                    </TableCell>

                    <TableCell className="text-right whitespace-nowrap">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-foreground"
                          onClick={() => onEdit?.(transaction)}
                          title="Editar registro"
                          aria-label="Editar registro"
                        >
                          <Pencil className="size-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-destructive"
                          onClick={() => onDelete?.(transaction)}
                          title="Eliminar registro"
                          aria-label="Eliminar registro"
                        >
                          <Trash2 className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}
      </div>
    </div>
  )
})
